import _cloneDeep from 'lodash/cloneDeep';
import _merge from 'lodash/merge';
import EntityGrid from 'src/components/EntityGrid';
import AtlasProductCellFormatter from 'src/components/EntityGrid/Table/AtlasProductCellFormatter';
import MarketShareGrid from 'src/components/EntityGrid/Table/MarketShareGrid';
import WeeklyMetricsGridContainer from 'src/components/EntityGrid/Table/WeeklyMetricsGridContainer';
import GenericPageContainer from 'src/components/EntityPage/GenericPageContainer';
import {
  buildTopEntitiesWidgetConfig,
  buildTrendWidgetConfig,
  buildMultiGroupByTopEntitiesChartWidgetConfig
} from 'src/components/Layout/LayoutUtil';
import { DATATYPE, ENTITIES, INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { addProp, filterNils } from 'src/utils/fp';
import VIEWS from '../ViewDefaultConfig';
import { getGroupByMapping } from './ConversionPageLayout';
import { shouldShowOnePRetailSales } from '../../../utils/app';
import MultiMetricTrendChart from 'src/components/EntityPage/TrendChart/MultiMetricTrendChart';

const MINIMUM_NUMBER_OF_SKUS_FOR_ONE_P_SEGMENT = 25;
export const getRetailSalesAggregationFields = ({ app, indexName = 'sales' }) => {
  const aggregationFields = ['retailSales', 'retailPrice', 'unitsSold'].map((fieldName) =>
    INDEX_FIELDS.getField(app.name, indexName, fieldName, 'product', 'stacklineSku')
  );

  const metricFields = aggregationFields.map(addProp('cellRendererFramework', AtlasProductCellFormatter));

  return { aggregationFields, metricFields };
};

const getMarketShareFieldsForRetailSales = (app, entity, weekIdField) => {
  const retailSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'retailSalesMarketShare',
    entity.type,
    weekIdField.name
  );
  retailSalesMarketShareField.numerator = 'retailSales';
  const onePSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'onePSalesMarketShare',
    entity.type,
    weekIdField.name
  );
  onePSalesMarketShareField.numerator = 'onePSales';
  const threePSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'threePSalesMarketShare',
    entity.type,
    weekIdField.name
  );
  threePSalesMarketShareField.numerator = 'threePSales';

  const onePRetailSalesMarketShareField = _cloneDeep(onePSalesMarketShareField);
  onePRetailSalesMarketShareField.denominator = 'retailSales';
  onePRetailSalesMarketShareField.displayName = '1P Market Share of Retail Sales';
  onePRetailSalesMarketShareField.numerator = 'onePSales';

  const threePRetailSalesMarketShareField = _cloneDeep(threePSalesMarketShareField);
  threePRetailSalesMarketShareField.denominator = 'retailSales';
  threePRetailSalesMarketShareField.displayName = '3P Market Share of Retail Sales';
  threePRetailSalesMarketShareField.numerator = 'threePSales';
  return [
    retailSalesMarketShareField,
    onePRetailSalesMarketShareField,
    onePSalesMarketShareField,
    threePRetailSalesMarketShareField,
    threePSalesMarketShareField
  ];
};

const getMarketShareFieldsForUnitsSold = (app, entity, weekIdField) => {
  const unitsSoldMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'unitsSoldMarketShare',
    entity.type,
    weekIdField.name
  );
  unitsSoldMarketShareField.numerator = 'unitsSold';
  const onePUnitsSoldMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'onePUnitsSoldMarketShare',
    entity.type,
    weekIdField.name
  );
  onePUnitsSoldMarketShareField.numerator = 'onePUnitsSold';
  const threePUnitsSoldMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'threePUnitsSoldMarketShare',
    entity.type,
    weekIdField.name
  );
  threePUnitsSoldMarketShareField.numerator = 'threePUnitsSold';

  const onePByUnitsSoldMarketShareField = _cloneDeep(onePUnitsSoldMarketShareField);
  onePByUnitsSoldMarketShareField.denominator = 'unitsSold';
  onePByUnitsSoldMarketShareField.displayName = '1P Market Share of Units Sold';
  onePByUnitsSoldMarketShareField.numerator = 'onePUnitsSold';

  const threePByUnitsSoldMarketShareField = _cloneDeep(threePUnitsSoldMarketShareField);
  threePByUnitsSoldMarketShareField.denominator = 'unitsSold';
  threePByUnitsSoldMarketShareField.displayName = '3P Market Share of Units Sold';
  threePByUnitsSoldMarketShareField.numerator = 'threePUnitsSold';

  return [
    unitsSoldMarketShareField,
    onePByUnitsSoldMarketShareField,
    onePUnitsSoldMarketShareField,
    threePByUnitsSoldMarketShareField,
    threePUnitsSoldMarketShareField
  ];
};

const buildMultiMetricTrendChartForFields = ({
  entity,
  app,
  showOnlyMainSeries,
  indexName,
  fieldNames,
  nameOfTrendChart
}) => {
  const groupByFieldName = 'weekId';
  const metricFieldNames = filterNils([...fieldNames]);
  const name = nameOfTrendChart;
  const weekIdFieldOverride = INDEX_FIELDS.getField(app.name, indexName, 'weekId', entity.type);

  const widget = buildTrendWidgetConfig(
    app,
    indexName,
    entity,
    groupByFieldName,
    metricFieldNames,
    weekIdFieldOverride,
    {
      CustomComponent: MultiMetricTrendChart,
      name,
      view: {
        name,
        chartPropsOverride: {
          title: { text: '' }
        },
        showOnlyMainSeries,
        hideSubtitle: true
      }
    }
  );

  widget.view.metricFields = widget.view.metricFields.map((metricField) => ({
    ...metricField,
    displayName: metricField.displayName || ''
  }));

  return widget;
};

const buildMultiMetricTrendChartForMarketShare = ({ entity, app, showOnlyMainSeries, indexName }) => {
  const groupByFieldName = 'weekId';
  const RETAIL_SALES_METRIC_FIELD_NAMES = ['retailSales', 'onePSales', 'threePSales'];
  const metricFieldNames = filterNils([...RETAIL_SALES_METRIC_FIELD_NAMES]);
  const name = 'multiMetricMarketShareBySales';
  const weekIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'weekId'));
  const retailSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'retailSalesMarketShare',
    entity.type,
    weekIdField.name
  );
  const onePSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'onePSalesMarketShare',
    entity.type,
    weekIdField.name
  );
  const threePSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'threePSalesMarketShare',
    entity.type,
    weekIdField.name
  );

  const onePRetailSalesMarketShareField = _cloneDeep(onePSalesMarketShareField);
  onePRetailSalesMarketShareField.denominator = 'retailSales';
  onePRetailSalesMarketShareField.displayName = '1P Market Share of Retail Sales';
  onePRetailSalesMarketShareField.numerator = 'onePSales';

  const threePRetailSalesMarketShareField = _cloneDeep(threePSalesMarketShareField);
  threePRetailSalesMarketShareField.denominator = 'retailSales';
  threePRetailSalesMarketShareField.displayName = '3P Market Share of Retail Sales';
  threePRetailSalesMarketShareField.numerator = 'threePSales';

  const widget = buildTrendWidgetConfig(app, indexName, entity, groupByFieldName, metricFieldNames, weekIdField, {
    CustomComponent: MultiMetricTrendChart,
    name,
    view: {
      name,
      metricFields: [
        retailSalesMarketShareField,
        onePRetailSalesMarketShareField,
        onePSalesMarketShareField,
        threePRetailSalesMarketShareField,
        threePSalesMarketShareField
      ],
      chartPropsOverride: {
        title: { text: '' }
      },
      showOnlyMainSeries,
      hideSubtitle: true
    },
    data: {
      marketShare: { compute: true },
      multipleFields: true
    }
  });

  widget.view.metricFields = widget.view.metricFields.map((metricField) => ({
    ...metricField,
    displayName: metricField.displayName || ''
  }));

  return widget;
};

const buildMultiMetricTrendChartForUnitsSoldMarketShare = ({ entity, app, showOnlyMainSeries, indexName }) => {
  const groupByFieldName = 'weekId';
  const UNITS_SOLD_METRIC_FIELD_NAMES = ['unitsSold', 'onePUnitsSold', 'threePUnitsSold'];
  const metricFieldNames = filterNils([...UNITS_SOLD_METRIC_FIELD_NAMES]);
  const name = 'multiMetricMarketShareByUnitsSold';
  const weekIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'weekId'));
  const marketShareMetricFieldsForUnitsSold = getMarketShareFieldsForUnitsSold(app, entity, weekIdField);

  const widget = buildTrendWidgetConfig(app, indexName, entity, groupByFieldName, metricFieldNames, weekIdField, {
    CustomComponent: MultiMetricTrendChart,
    name,
    view: {
      name,
      metricFields: marketShareMetricFieldsForUnitsSold,
      chartPropsOverride: {
        title: { text: '' }
      },
      showOnlyMainSeries,
      hideSubtitle: true
    },
    data: {
      marketShare: { compute: true },
      multipleFields: true
    }
  });

  widget.view.metricFields = widget.view.metricFields.map((metricField) => ({
    ...metricField,
    displayName: metricField.displayName || ''
  }));

  return widget;
};

const buildMultiMetricTrendChartForOnePShareOfSales = ({ entity, app, showOnlyMainSeries, indexName }) => {
  const groupByFieldName = 'weekId';
  const RETAIL_SALES_METRIC_FIELD_NAMES = ['retailSales', 'onePSales', 'threePSales'];
  const metricFieldNames = filterNils([...RETAIL_SALES_METRIC_FIELD_NAMES]);
  const name = 'multiMetricOnePShareBySales';
  const weekIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'weekId'));
  const onePSalesMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'onePSalesMarketShare',
    entity.type,
    weekIdField.name
  );

  const onePRetailSalesShareField = _cloneDeep(onePSalesMarketShareField);
  onePRetailSalesShareField.denominator = 'retailSales';
  onePRetailSalesShareField.displayName = '1P Retail Sales as % of Total';
  onePRetailSalesShareField.numerator = 'onePSales';

  const widget = buildTrendWidgetConfig(app, indexName, entity, groupByFieldName, metricFieldNames, weekIdField, {
    CustomComponent: MultiMetricTrendChart,
    name,
    view: {
      denominatorConditionsEqualToMainEntity: true,
      preventConditionsUpdate: true,
      name,
      metricFieldsToDisplayInDropDown: [onePRetailSalesShareField],
      preventDropDown: true,
      metricFields: [onePRetailSalesShareField],
      chartPropsOverride: {
        title: { text: '1P Retail Sales as % of Total' }
      },
      showOnlyMainSeries,
      hideSubtitle: true
    },
    data: {
      marketShare: { compute: true }
    }
  });

  widget.view.metricFields = widget.view.metricFields.map((metricField) => ({
    ...metricField,
    displayName: metricField.displayName || ''
  }));

  return widget;
};

const buildMultiMetricTrendChartForOnePShareOfUnitsSold = ({ entity, app, showOnlyMainSeries, indexName }) => {
  const groupByFieldName = 'weekId';
  const RETAIL_SALES_METRIC_FIELD_NAMES = ['unitsSold', 'onePUnitsSold', 'threePUnitsSold'];
  const metricFieldNames = filterNils([...RETAIL_SALES_METRIC_FIELD_NAMES]);
  const name = 'multiMetricOnePShareByUnitsSold';
  const weekIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'weekId'));
  const onePUnitsSoldMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'onePUnitsSoldMarketShare',
    entity.type,
    weekIdField.name
  );

  const onePUnitsSoldShareField = _cloneDeep(onePUnitsSoldMarketShareField);
  onePUnitsSoldShareField.denominator = 'unitsSold';
  onePUnitsSoldShareField.displayName = '1P Units Sold as % of Total';
  onePUnitsSoldShareField.numerator = 'onePUnitsSold';

  const widget = buildTrendWidgetConfig(app, indexName, entity, groupByFieldName, metricFieldNames, weekIdField, {
    CustomComponent: MultiMetricTrendChart,
    name,
    view: {
      denominatorConditionsEqualToMainEntity: true,
      preventConditionsUpdate: true,
      name,
      metricFieldsToDisplayInDropDown: [onePUnitsSoldShareField],
      preventDropDown: true,
      metricFields: [onePUnitsSoldShareField],
      chartPropsOverride: {
        title: { text: '1P Units Sold as % of Total' }
      },
      showOnlyMainSeries,
      hideSubtitle: true
    },
    data: {
      marketShare: { compute: true }
    }
  });

  widget.view.metricFields = widget.view.metricFields.map((metricField) => ({
    ...metricField,
    displayName: metricField.displayName || ''
  }));

  return widget;
};

export default function getSalesPageLayout({ app, retailer, user, tab, metricType, entity }) {
  const indexName = tab;
  const weekIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'weekId'));
  const retailerIdField = _cloneDeep(INDEX_FIELDS.getField(app.name, indexName, 'retailerId'));
  const dataConfig = {
    indexName,
    retailerEntity: _cloneDeep(ENTITIES.atlas.retailer),
    categoryEntity: _cloneDeep(ENTITIES.atlas.category),
    subcategoryEntity: _cloneDeep(ENTITIES.atlas.category),
    relatedEntity: _cloneDeep(ENTITIES.atlas.brand),
    weekIdField,
    retailerIdField
  };
  const onAllCategoriesPage = entity.type === 'category' && entity.id === '0';
  const retailPriceMarketShareField = INDEX_FIELDS.getField(
    app.name,
    'sales',
    'retailPriceMarketShare',
    entity.type,
    weekIdField.name
  );
  const widgets = [];
  const fields = ['retailSales', 'retailPrice', 'unitsSold'];
  if (fields.indexOf(metricType) > 0) {
    fields.splice(fields.indexOf(metricType), 1);
    fields.unshift(metricType);
  }
  let widgetConfig = buildTrendWidgetConfig(app, indexName, entity, 'weekId', [...fields], weekIdField, {
    data: {
      metricName: 'mainEntityMetrics'
    }
  });
  if (widgetConfig.name === 'retailPriceByweekId') {
    widgetConfig.data.avoidZeroes = true;
  }

  const displayOnePThreePData = () => {
    if (
      (metricType === 'retailSales' || metricType === 'unitsSold') &&
      shouldShowOnePRetailSales()) {
      if (
        (entity.type === 'segment' && entity.skuCount < MINIMUM_NUMBER_OF_SKUS_FOR_ONE_P_SEGMENT) ||
        entity.type === 'product'
      ) {
        return false;
      }
      return true;
    }
    return false;
  };

  if (!displayOnePThreePData()) {
    widgets.push(widgetConfig);
  }

  const fieldsForSales = ['retailSales', 'onePSales', 'threePSales'];
  const fieldsForUnitsSold = ['unitsSold', 'onePUnitsSold', 'threePUnitsSold'];
  if (displayOnePThreePData()) {
    // adds widget for displaying the multi trend chart with retail sales, 1P retail sales, 3p retail sales on the retail sales page
    if (metricType === 'retailSales') {
      const widgetConfigForMultiTrendChart = buildMultiMetricTrendChartForFields({
        entity,
        app,
        showOnlyMainSeries: false,
        indexName: tab,
        fieldNames: fieldsForSales,
        nameOfTrendChart: 'multiMetricTrendChartForSales'
      });
      widgets.push(widgetConfigForMultiTrendChart);
    }
    if (metricType === 'unitsSold') {
      const widgetConfigForMultiTrendChart = buildMultiMetricTrendChartForFields({
        entity,
        app,
        showOnlyMainSeries: false,
        indexName: tab,
        fieldNames: fieldsForUnitsSold,
        nameOfTrendChart: 'multiMetricTrendChartForUnitsSold'
      });
      widgets.push(widgetConfigForMultiTrendChart);
    }
  }
  if (metricType === 'retailPrice' && !onAllCategoriesPage) {
    widgets.push(
      buildTrendWidgetConfig(app, 'sales', entity, 'weekId', ['retailPrice'], weekIdField, {
        view: {
          displayName: retailPriceMarketShareField.displayName,
          metricFields: [retailPriceMarketShareField],
          chartPropsOverride: {
            title: { text: 'Price Index' }
          }
        },
        data: {
          marketShare: {
            compute: true,
            metricType: METRICTYPE.MULTIPLE,
            showMarketShareTooltip: false
          }
        }
      })
    );
  }

  if (entity.type === 'businessunit' && tab === 'sales' && metricType === 'retailPrice') {
    widgets.push(
      buildTopEntitiesWidgetConfig(
        app,
        indexName,
        entity,
        [
          {
            groupByFieldName: 'brandId',
            aggregationFieldNames: ['retailPrice']
          }
        ],
        50,
        weekIdField,
        {
          view: {
            chartPropsOverride: {
              horizontalScrolling: {
                enabled: true,
                step: 3
              },
              xAxis: [
                {
                  min: 0,
                  max: 9
                }
              ]
            }
          }
        },
        {
          aggregateByFieldDisplayName: 'Retail Sales',
          aggregateByFieldName: 'retailSales',
          function: 'sum',
          canBeExported: true
        }
      )
    );
  }

  if (metricType !== 'retailPrice' && tab !== 'promotions') {
    widgetConfig = buildTrendWidgetConfig(app, indexName, entity, 'weekId', ['retailPrice'], weekIdField, {});
    if (widgetConfig.name === 'retailPriceByweekId') {
      widgetConfig.data.avoidZeroes = true;
    }
    widgets.push(widgetConfig); // replace Above
  }

  if (
    entity.type !== 'category' &&
    entity.type !== 'subcategory' &&
    entity.type !== 'segment' &&
    entity.type !== 'businessunit' &&
    tab !== 'promotions' &&
    metricType !== 'retailPrice'
  ) {
    if (metricType === 'retailSales') {
      if (!displayOnePThreePData()) {
        const retailSalesMarketShareField = INDEX_FIELDS.getField(
          app.name,
          'sales',
          'retailSalesMarketShare',
          entity.type,
          weekIdField.name
        );

        widgetConfig = buildTrendWidgetConfig(app, 'sales', entity, 'weekId', ['retailSales'], weekIdField, {
          name: 'marketShareBySales',
          view: {
            displayName: retailSalesMarketShareField.displayName,
            metricFields: [retailSalesMarketShareField],
            chartPropsOverride: {}
          },
          data: {
            marketShare: { compute: true }
          }
        });

        widgets.push(widgetConfig);
      } else {
        widgetConfig = buildMultiMetricTrendChartForMarketShare({
          entity,
          app,
          showOnlyMainSeries: false,
          indexName: tab
        });
        widgets.push(widgetConfig);
      }
    } else if (metricType === 'unitsSold') {
      if (!displayOnePThreePData()) {
        const unitsSoldMarketShareField = INDEX_FIELDS.getField(
          app.name,
          'sales',
          'unitsSoldMarketShare',
          entity.type,
          weekIdField.name
        );

        widgets.push(
          buildTrendWidgetConfig(app, 'sales', entity, 'weekId', ['unitsSold'], weekIdField, {
            name: 'marketShareByUnits',
            displayName: unitsSoldMarketShareField.displayName,
            metricFields: [unitsSoldMarketShareField],
            view: {
              displayName: unitsSoldMarketShareField.displayName,
              metricFields: [unitsSoldMarketShareField],
              chartPropsOverride: {}
            },
            data: {
              marketShare: { compute: true }
            }
          })
        );
      } else {
        const widgetConfigForMultiMetricMarketShare = buildMultiMetricTrendChartForUnitsSoldMarketShare({
          entity,
          app,
          showOnlyMainSeries: false,
          indexName: tab
        });
        widgets.push(widgetConfigForMultiMetricMarketShare);
      }
    }
  }

  if (user.config && !user.config.isRestrictedDemoUser) {
    if (entity.type !== 'product' && tab === 'sales' && (metricType === 'retailSales' || metricType === 'unitsSold')) {
      const productsWithSalesWidgetConfig = buildTrendWidgetConfig(
        app,
        'sales',
        entity,
        'weekId',
        ['stacklineSku'],
        weekIdField,
        {
          name: 'productCountMetric',
          view: {
            chartPropsOverride: {
              title: { text: 'Product Count' }
            }
          }
        }
      );
      productsWithSalesWidgetConfig.data.configByGroupByFieldName.weekId.aggregationFields.find(
        (x) => x.name === 'stacklineSku'
      ).conditions = {
        rangeFilters: [
          {
            fieldName: 'unitsSold',
            minValue: 4
          }
        ]
      };

      productsWithSalesWidgetConfig.view.metricFields[0].metricType = METRICTYPE.VOLUME;
      productsWithSalesWidgetConfig.view.metricFields[0].dataType = DATATYPE.INTEGER;
      const stacklineSkuField =
        productsWithSalesWidgetConfig.data.configByGroupByFieldName.weekId.aggregationFields.find(
          (x) => x.name === 'stacklineSku'
        );
      stacklineSkuField.metricType = METRICTYPE.VOLUME;
      stacklineSkuField.dataType = DATATYPE.INTEGER;

      widgets.push(productsWithSalesWidgetConfig);
    }

    if (
      entity.type !== 'company' &&
      entity.type !== 'product' &&
      tab !== 'promotions' &&
      metricType !== 'retailPrice'
    ) {
      if (entity.type !== 'brand') {
        let aggregationFieldMappingMarketShare = [];
        aggregationFieldMappingMarketShare = [
          {
            groupByFieldName: 'brandId',
            aggregationFieldNames: [metricType]
          },
          {
            groupByFieldName: 'parentBrandId',
            aggregationFieldNames: [metricType]
          }
        ];

        if (!displayOnePThreePData()) {
          widgets.push(
            buildTopEntitiesWidgetConfig(
              app,
              indexName,
              entity,
              aggregationFieldMappingMarketShare,
              50, // this is page size
              weekIdField,
              {
                isMarketShare: true,
                view: {
                  enableComparison: true,
                  metricFields: [INDEX_FIELDS.getField(app.name, indexName, `${metricType}MarketShare`, entity.type)],
                  chartPropsOverride: {
                    title: { text: 'Market Share by ' },
                    horizontalScrolling: {
                      enabled: true,
                      step: 3
                    },
                    xAxis: [
                      {
                        min: 0,
                        max: 9
                      }
                    ],
                    enableSwitchingGroupBy: true
                  }
                }
              }
            )
          );
        }
        const aggregationFieldMappingForMultiGroup = [];
        aggregationFieldMappingMarketShare.forEach((fieldMapping) => {
          const temp = { ...fieldMapping };
          // @ts-ignore
          temp.indexName = indexName;
          aggregationFieldMappingForMultiGroup.push(temp);
        });

        if (displayOnePThreePData()) {
          if (metricType === 'retailSales') {
            const marketShareMetricFields = getMarketShareFieldsForRetailSales(app, entity, weekIdField);
            const aggregationFieldsForMarketShare = ['retailSales', 'onePSales', 'threePSales'].map((fieldName) =>
              INDEX_FIELDS.getField(app.name, indexName, fieldName, entity.type, 'stacklineSku')
            );
            const nameForWidget = 'multiGroupByTopEntitiesChartForMarketShareSales';
            const widgetConfigForMultiGroupByMarketShareSales = buildMultiGroupByTopEntitiesChartWidgetConfig(
              app,
              indexName,
              entity,
              aggregationFieldMappingForMultiGroup,
              50,
              marketShareMetricFields,
              weekIdField,
              {
                isMarketShare: true,
                name: nameForWidget,
                view: {
                  name: nameForWidget,
                  sortByMainMetric: true,
                  aggregationFieldsForMarketShare,
                  enableComparison: true,
                  anchorName: 'multiGroupByTopEntitiesChartForMarketShareSales',
                  chartPropsOverride: {
                    title: { text: 'Market Share by' },
                    horizontalScrolling: {
                      enabled: true,
                      step: 3
                    },
                    xAxis: [
                      {
                        min: 0,
                        max: 9
                      }
                    ],
                    enableSwitchingGroupBy: true,
                    anchorName: 'multiGroupByTopEntitiesChartForMarketShareSales'
                  }
                },
                data: {
                  sortByAggregationField: {
                    aggregateByFieldDisplayName: 'Retail Sales',
                    aggregateByFieldName: 'retailSales',
                    function: 'sum',
                    canBeExported: true
                  }
                }
              }
            );
            widgets.push(widgetConfigForMultiGroupByMarketShareSales);
          } else if (metricType === 'unitsSold') {
            const marketShareMetricFields = getMarketShareFieldsForUnitsSold(app, entity, weekIdField);
            const aggregationFieldsForMarketShare = ['unitsSold', 'onePUnitsSold', 'threePUnitsSold'].map((fieldName) =>
              INDEX_FIELDS.getField(app.name, indexName, fieldName, entity.type, 'stacklineSku')
            );
            const nameForWidget = 'multiGroupByTopEntitiesChartForMarketShareUnitsSold';
            const widgetConfigForMultiGroupByMarketShareUnitsSold = buildMultiGroupByTopEntitiesChartWidgetConfig(
              app,
              indexName,
              entity,
              aggregationFieldMappingForMultiGroup,
              50,
              marketShareMetricFields,
              weekIdField,
              {
                isMarketShare: true,
                name: nameForWidget,
                view: {
                  name: nameForWidget,
                  sortByMainMetric: true,
                  aggregationFieldsForMarketShare,
                  enableComparison: true,
                  anchorName: 'multiGroupByTopEntitiesChartForMarketShareUnitsSold',
                  chartPropsOverride: {
                    title: { text: 'Market Share by' },
                    horizontalScrolling: {
                      enabled: true,
                      step: 3
                    },
                    xAxis: [
                      {
                        min: 0,
                        max: 9
                      }
                    ],
                    enableSwitchingGroupBy: true,
                    anchorName: 'multiGroupByTopEntitiesChartForMarketShareUnitsSold'
                  }
                },
                data: {
                  sortByAggregationField: {
                    aggregateByFieldDisplayName: 'Units Sold',
                    aggregateByFieldName: 'unitsSold',
                    function: 'sum',
                    canBeExported: true
                  }
                }
              }
            );
            widgets.push(widgetConfigForMultiGroupByMarketShareUnitsSold);
          }
        }
      }

      widgets.push({
        name: 'marketShareGrid',
        view: VIEWS.marketShareGrid,
        CustomComponent: MarketShareGrid,
        data: {
          groupByFields: [INDEX_FIELDS.getField(app.name, indexName, dataConfig.relatedEntity.keyFieldName)],
          configByGroupByFieldName: {
            brandId: {
              indexName,
              entity: ENTITIES.atlas.brand,
              mainMetricField: INDEX_FIELDS.getField(app.name, indexName, metricType, entity.type),
              aggregationFields: [INDEX_FIELDS.getField(app.name, indexName, metricType, entity.type)],
              isPrimarySource: true
            }
          },
          weekIdField: INDEX_FIELDS.getField(app.name, indexName, 'weekId')
        }
      });
    }
  }

  if (!['product'].includes(entity.type)) {
    const noOfTopEntities = 50;
    const allowHorizontalScroll = {
      horizontalScrolling: {
        enabled: true,
        step: 3
      },
      xAxis: [
        {
          min: 0,
          max: 9
        }
      ]
    };

    if (['company', 'brand', 'category', 'subcategory', 'segment', 'businessunit'].includes(entity.type)) {
      const { category, subcategory, brand, parentBrand } = getGroupByMapping(metricType);
      let aggregationFieldMapping = {};
      aggregationFieldMapping = {
        company: [brand, category, subcategory, parentBrand],
        category: [brand, subcategory, parentBrand],
        subcategory: [brand, parentBrand],
        brand: [category, subcategory],
        segment: [brand, category, subcategory, parentBrand],
        businessunit: [brand, category, subcategory, parentBrand]
      };
      if (!displayOnePThreePData()) {
        widgets.push(
          buildTopEntitiesWidgetConfig(
            app,
            indexName,
            entity,
            aggregationFieldMapping[entity.type],
            noOfTopEntities,
            weekIdField,
            {
              view: {
                enableComparison: true,
                chartPropsOverride: {
                  ...allowHorizontalScroll,
                  enableSwitchingGroupBy: true
                }
              }
            }
          )
        );
      }
      if (displayOnePThreePData()) {
        if (metricType === 'retailSales') {
          const aggregationFieldMappingForMultiGroup = [];
          const nameForWidget = 'multiGroupByTopEntitiesChartForSales';
          aggregationFieldMapping[entity.type].forEach((fieldMapping) => {
            const temp = { ...fieldMapping };
            // @ts-ignore
            temp.indexName = indexName;
            aggregationFieldMappingForMultiGroup.push(temp);
          });
          const retailSalesMetricFields = ['retailSales', 'onePSales', 'threePSales'].map((fieldName) =>
            INDEX_FIELDS.getField(app.name, indexName, fieldName, entity.type, 'stacklineSku')
          );
          const widgetConfigForMultiGroupSales = buildMultiGroupByTopEntitiesChartWidgetConfig(
            app,
            indexName,
            entity,
            aggregationFieldMappingForMultiGroup,
            50,
            retailSalesMetricFields,
            weekIdField,
            {
              name: nameForWidget,
              view: {
                name: nameForWidget,
                enableComparison: true,
                sortByMainMetric: true,
                anchorName: 'multiGroupByTopEntitiesChartForSales',
                chartPropsOverride: {
                  horizontalScrolling: {
                    enabled: true,
                    step: 3
                  },
                  xAxis: [
                    {
                      min: 0,
                      max: 9
                    }
                  ],
                  enableSwitchingGroupBy: true,
                  anchorName: 'multiGroupByTopEntitiesChartForSales'
                }
              },
              data: {
                sortByAggregationField: {
                  aggregateByFieldDisplayName: 'Retail Sales',
                  aggregateByFieldName: 'retailSales',
                  function: 'sum',
                  canBeExported: true
                }
              }
            }
          );
          widgets.push(widgetConfigForMultiGroupSales);
          const widgetConfigForOnePShareOfSales = buildMultiMetricTrendChartForOnePShareOfSales({
            entity,
            app,
            showOnlyMainSeries: false,
            indexName: tab
          });
          widgets.push(widgetConfigForOnePShareOfSales);
        }
        if (metricType === 'unitsSold') {
          {
            const aggregationFieldMappingForMultiGroup = [];
            const nameForWidget = 'multiGroupByTopEntitiesChartForUnitsSold';
            aggregationFieldMapping[entity.type].forEach((fieldMapping) => {
              const temp = { ...fieldMapping };
              // @ts-ignore
              temp.indexName = indexName;
              aggregationFieldMappingForMultiGroup.push(temp);
            });
            const retailSalesMetricFields = ['unitsSold', 'onePUnitsSold', 'threePUnitsSold'].map((fieldName) =>
              INDEX_FIELDS.getField(app.name, indexName, fieldName, entity.type, 'stacklineSku')
            );
            const widgetConfigForMultiGroupUnitsSold = buildMultiGroupByTopEntitiesChartWidgetConfig(
              app,
              indexName,
              entity,
              aggregationFieldMappingForMultiGroup,
              50,
              retailSalesMetricFields,
              weekIdField,
              {
                name: nameForWidget,
                view: {
                  name: nameForWidget,
                  enableComparison: true,
                  sortByMainMetric: true,
                  anchorName: 'multiGroupByTopEntitiesChartForUnitsSold',
                  chartPropsOverride: {
                    horizontalScrolling: {
                      enabled: true,
                      step: 3
                    },
                    xAxis: [
                      {
                        min: 0,
                        max: 9
                      }
                    ],
                    enableSwitchingGroupBy: true,
                    anchorName: 'multiGroupByTopEntitiesChartForUnitsSold'
                  }
                },
                data: {
                  sortByAggregationField: {
                    aggregateByFieldDisplayName: 'Units Sold',
                    aggregateByFieldName: 'unitsSold',
                    function: 'sum',
                    canBeExported: true
                  }
                }
              }
            );
            widgets.push(widgetConfigForMultiGroupUnitsSold);
            const widgetConfigForOnePShareOfUnitsSold = buildMultiMetricTrendChartForOnePShareOfUnitsSold({
              entity,
              app,
              showOnlyMainSeries: false,
              indexName: tab
            });
            widgets.push(widgetConfigForOnePShareOfUnitsSold);
          }
        }
      }
    } else {
      widgets.push(
        buildTopEntitiesWidgetConfig(
          app,
          indexName,
          entity,
          [
            {
              groupByFieldName: 'subCategoryId',
              aggregationFieldNames: [metricType]
            }
          ],
          noOfTopEntities,
          weekIdField,
          {
            replaceSubCategoryWithParent: true,
            view: {
              enableComparison: true,
              chartPropsOverride: allowHorizontalScroll
            }
          }
        )
      );
    }
  }

  if (user.config && !user.config.isRestrictedDemoUser) {
    const { aggregationFields, metricFields } = getRetailSalesAggregationFields({ app, retailer, indexName });

    if (entity.type !== 'product' && tab !== 'promotions' && entity.type !== 'brand') {
      widgets.push({
        name: 'entityGrid',
        CustomComponent: EntityGrid,
        view: _merge({}, VIEWS.entityGrid, {
          gridOptions: {}
        }),
        data: {
          defaultSortField: INDEX_FIELDS.getField(
            app.name,
            'sales',
            metricType === 'retailSales' ? 'retailSales' : 'unitsSold',
            'product',
            'stacklineSku'
          ),
          groupByFields: [INDEX_FIELDS.getField(app.name, indexName, 'stacklineSku', 'product')],
          configByGroupByFieldName: {
            stacklineSku: {
              indexName,
              entity: ENTITIES.atlas.product,
              mainMetricField: INDEX_FIELDS.getField(app.name, indexName, metricType, 'product', 'stacklineSku'),
              aggregationFields: [
                INDEX_FIELDS.getField(app.name, indexName, 'retailSales', 'product', 'stacklineSku'),
                INDEX_FIELDS.getField(app.name, indexName, 'retailPrice', 'product', 'stacklineSku'),
                INDEX_FIELDS.getField(app.name, indexName, 'unitsSold', 'product', 'stacklineSku')
              ],
              tableView: {
                metricFields
              }
            }
          }
        }
      });
    }

    if (entity.type === 'brand' && tab !== 'promotions') {
      widgets.push({
        name: 'entityGrid',
        CustomComponent: EntityGrid,
        view: _merge({}, VIEWS.entityGrid, {
          gridOptions: {}
        }),
        data: {
          groupByFields: [INDEX_FIELDS.getField(app.name, indexName, 'stacklineSku', 'product')],
          configByGroupByFieldName: {
            stacklineSku: {
              indexName,
              entity: ENTITIES.atlas.product,
              mainMetricField: INDEX_FIELDS.getField(app.name, indexName, metricType, 'product', 'stacklineSku'),
              aggregationFields,
              tableView: {
                metricFields
              }
            }
          }
        }
      });
    }

    if (entity.type === 'product') {
      widgets.push({
        view: VIEWS.weeklyMetricsGridContainer,
        CustomComponent: WeeklyMetricsGridContainer,
        data: {
          avoidZeroes: false,
          entity: ENTITIES.atlas.product,
          chartMainField: INDEX_FIELDS.getField(
            app.name,
            indexName,
            metricType,
            entity.type,
            dataConfig.weekIdField.name
          ),
          aggregationFields: [
            INDEX_FIELDS.getField(app.name, indexName, 'unitsSold', entity.type, dataConfig.weekIdField.name),
            INDEX_FIELDS.getField(app.name, indexName, 'retailSales', entity.type, dataConfig.weekIdField.name),
            INDEX_FIELDS.getField(app.name, indexName, 'retailPrice', entity.type, dataConfig.weekIdField.name)
          ],
          groupByField: dataConfig.weekIdField
        }
      });
    }
  }
  return {
    CustomPageContainer: GenericPageContainer,
    enableComparison: true,
    dataConfig,
    widgets
  };
}
